import { hasEquipment, type ResourceImportDraft } from './types';

/** Per-row validation outcome. Errors block the row; warnings are advisory. */
export interface RowIssues {
  errors: string[];
  warnings: string[];
}

const EMPTY: RowIssues = { errors: [], warnings: [] };

/** Max lengths enforced by the BE validator — surfaced here so the operator
 *  fixes them in the preview instead of collecting 400s at commit. */
const MAX = { name: 256, displayName: 128, reference: 64, mobileNumber: 32, email: 320 };

const normalise = (v: string) => v.trim().toLowerCase();

/** Values (normalised) appearing on more than one row, per field. */
function duplicatesOf(drafts: ResourceImportDraft[], pick: (d: ResourceImportDraft) => string) {
  const counts = new Map<string, number>();
  for (const d of drafts) {
    const v = normalise(pick(d));
    if (!v) continue;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  const dups = new Set<string>();
  for (const [v, n] of counts) if (n > 1) dups.add(v);
  return dups;
}

export interface DuplicateSets {
  names: Set<string>;
  references: Set<string>;
}

export function findInFileDuplicates(drafts: ResourceImportDraft[]): DuplicateSets {
  return {
    names: duplicatesOf(drafts, (d) => d.name),
    references: duplicatesOf(drafts, (d) => d.reference),
  };
}

export function validateDraft(d: ResourceImportDraft, dups: DuplicateSets): RowIssues {
  const errors: string[] = [];
  const warnings: string[] = [];

  const name = d.name.trim();
  if (!name) errors.push('Name is required');
  if (name.length > MAX.name) errors.push(`Name is longer than ${MAX.name} characters`);
  if (d.displayName.trim().length > MAX.displayName)
    errors.push(`Display name is longer than ${MAX.displayName} characters`);
  if (d.reference.trim().length > MAX.reference)
    errors.push(`Reference is longer than ${MAX.reference} characters`);
  if (d.mobileNumber.trim().length > MAX.mobileNumber)
    errors.push(`Mobile number is longer than ${MAX.mobileNumber} characters`);
  if (d.email.trim().length > MAX.email)
    errors.push(`Email is longer than ${MAX.email} characters`);

  if (name && dups.names.has(normalise(name))) {
    warnings.push('Duplicate name in file');
  }
  if (d.reference.trim() && dups.references.has(normalise(d.reference))) {
    warnings.push('Duplicate reference in file');
  }
  // Equipment type: unmatched text still imports (without a type) — flagged so
  // the operator can add it in Central Configuration and re-import if needed.
  if (hasEquipment(d.type)) {
    if (d.equipmentTypeText.trim() && !d.organisationEquipmentTypeId) {
      warnings.push(`No equipment type matches "${d.equipmentTypeText.trim()}" — will import without one`);
    } else if (!d.equipmentTypeText.trim()) {
      warnings.push('No equipment type');
    }
  }
  if (d.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())) {
    warnings.push('Email looks malformed');
  }

  return { errors, warnings };
}

/** Validate every draft → rowRef → issues. */
export function validateAll(drafts: ResourceImportDraft[]): Map<string, RowIssues> {
  const dups = findInFileDuplicates(drafts);
  const out = new Map<string, RowIssues>();
  for (const d of drafts) out.set(d.rowRef, validateDraft(d, dups));
  return out;
}

/** A row is committable when it has no blocking errors. */
export function isCommittable(issues: RowIssues | undefined): boolean {
  return (issues ?? EMPTY).errors.length === 0;
}
